import MapContainer from '@components/map/Map';
import {
  useFetchDashboardMap,
  useFetchProjectDashboardMap,
} from 'api/dashboard/dashboardServices';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  type CircleLayer,
  type GeoJSONSource,
  Layer,
  type MapLayerMouseEvent,
  type MapRef,
  Source,
} from 'react-map-gl';
import type { FeatureCollection } from 'geojson';
import { useTheme } from 'context/ThemeProvider/ThemeProvider';
import { useNavigate } from 'react-router-dom';
import { useMapUtilities } from 'hooks/useMapUtilities';
import { type Feature } from 'hooks/useMapUtilities/useMapUtilities';
import { type MetersMap } from 'interfaces/map/MetersMap.interface';
import { type ProjectMap } from 'interfaces/map/ProjectMap.interface';

interface DashboardMapProps {
  hoveredId?: number;
  setHoveredId?: (value?: number) => void;
}

const DashboardMap: FC<DashboardMapProps> = ({ hoveredId, setHoveredId }) => {
  const [averages, setAverages] = useState({ projects: 0, changes: 0 });
  const { data: changesData } = useFetchDashboardMap();
  const { data: projectsData } = useFetchProjectDashboardMap();
  const mapRef = useRef<MapRef | null>(null);

  const {
    defaultCircleLayerStyle,
    calculateAverageMapCircleSize,
    getMinMaxSizeValue,
    onHoverPainting,
  } = useMapUtilities(mapRef);

  const { theme } = useTheme();
  const navigate = useNavigate();

  useMemo(() => {
    if (changesData?.map_data) {
      const average = calculateAverageMapCircleSize<MetersMap>(
        changesData.map_data,
        'impact_average_diff_euro'
      );
      setAverages((current) => ({ ...current, changes: average }));
    }
  }, [changesData]);

  useMemo(() => {
    if (projectsData?.map_data) {
      const average = calculateAverageMapCircleSize<ProjectMap>(
        projectsData.map_data,
        'savings_euro'
      );
      setAverages((current) => ({ ...current, projects: average }));
    }
  }, [projectsData]);

  const changesGeojson = useMemo(() => {
    if (!changesData || !averages.changes) return;

    const parsedData: FeatureCollection = {
      type: 'FeatureCollection',
      features: changesData.map_data?.flatMap(
        (meter) =>
          [
            {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [meter.longitude, meter.latitude],
              },
              properties: {
                score: meter.total_score,
                id: meter.id,
                impactPerDay: meter.impact_average_diff_euro,
                avgBasedSize: getMinMaxSizeValue(
                  averages.changes,
                  meter.impact_average_diff_euro
                ),
              },
              maxZoom: 8,
            },
          ] ?? []
      ),
    };
    return parsedData;
  }, [changesData, averages.changes]);

  const projectsGeojson = useMemo(() => {
    if (!projectsData || !averages.projects) return;

    const parsedData: FeatureCollection = {
      type: 'FeatureCollection',
      features: projectsData.map_data?.flatMap(
        (project) =>
          [
            {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [project.longitude, project.latitude],
              },
              properties: {
                savingsEuro: project.savings_euro,
                id: project.id,
                avgBasedSize: getMinMaxSizeValue(
                  averages.projects,
                  project.savings_euro
                ),
              },
              maxZoom: 8,
            },
          ] ?? []
      ),
    };
    return parsedData;
  }, [projectsData, averages.projects]);

  const projectLayerStyle: CircleLayer = {
    ...defaultCircleLayerStyle,
    id: 'projects',
    paint: {
      ...defaultCircleLayerStyle.paint,
      'circle-color': theme.colors.blue,
      'circle-stroke-color': theme.colors.blue,
      'circle-opacity': 0.5,
    },
  };

  const layerStyle: CircleLayer = {
    ...defaultCircleLayerStyle,
    id: 'meters',
    paint: {
      ...defaultCircleLayerStyle.paint,
      'circle-color': theme.colors['primary-light'],
      'circle-stroke-color': theme.colors.primary,
    },
  };

  const coordinates = useMemo(() => {
    const changesCoordinates =
      changesData?.map_data?.map((coord) => [
        coord.longitude,
        coord.latitude,
      ]) ?? [];
    const projectsCoordinates =
      projectsData?.map_data?.map((coord) => [
        coord.longitude,
        coord.latitude,
      ]) ?? [];

    return [...changesCoordinates, ...projectsCoordinates];
  }, [changesData, projectsData]);

  const handleOnClickLayer = (event: MapLayerMouseEvent): void => {
    const map = mapRef.current;
    if (!map) return;

    const features = map.queryRenderedFeatures(event.point, {
      layers: ['meters', 'projects'],
    });

    if (features.length > 0) {
      const firstFeature = features[0];
      const layerId = firstFeature?.layer?.id;
      const id: string = firstFeature.properties?.id;

      if (layerId === 'meters') {
        navigate(`/app/operations/anomalies/details/${id}`);
      } else if (layerId === 'projects') {
        navigate(`/app/management/projects/details/${id}`);
      }
    }
  };

  useEffect(() => {
    if (mapRef?.current) {
      const map = mapRef.current;

      const metersSource = map.getSource('meters-data') as GeoJSONSource;
      const projectsSource = map.getSource('projects-data') as GeoJSONSource;

      const metersFeatures: Feature[] = (metersSource as any)?._data?.features;
      const projectsFeatures: Feature[] = (projectsSource as any)?._data
        ?.features;

      if (metersFeatures && metersFeatures.length > 0) {
        const hoveredFeature = metersFeatures.find(
          (feature) => feature.properties.id === hoveredId
        );

        onHoverPainting({
          hoveredFeature,
          layerId: 'meters',
          paintProperty: 'circle-color',
          hoveredId,
          hoveredColor: theme.colors.tertiary,
          defaultColor: theme.colors['primary-light'],
          border: {
            defaultColor: theme.colors.primary,
            hoveredColor: theme.colors['tertiary-dark'],
          },
        });
      }

      if (projectsFeatures && projectsFeatures.length > 0) {
        const hoveredFeature = projectsFeatures.find(
          (feature) => feature.properties.id === hoveredId
        );
        onHoverPainting({
          hoveredFeature,
          layerId: 'projects',
          paintProperty: 'circle-color',
          hoveredId,
          hoveredColor: theme.colors['light-blue'],
          defaultColor: theme.colors.blue,
          border: {
            defaultColor: theme.colors.blue,
            hoveredColor: theme.colors.blue,
          },
        });
      }
    }
  }, [hoveredId]);

  const layers = ['meters', 'projects'];
  mapRef.current?.on('mouseenter', layers, (event) => {
    const map = mapRef.current;
    if (!map) return;
    map.getCanvas().style.cursor = 'pointer';
    const hoveredMeterId = event.features?.[0]?.properties?.id;
    setHoveredId?.(hoveredMeterId);
  });

  mapRef.current?.on('mouseleave', layers, () => {
    const map = mapRef.current;
    if (!map) return;
    map.getCanvas().style.cursor = 'default';
    setHoveredId?.(undefined);
  });

  return (
    <div className="flex">
      <MapContainer
        ref={mapRef}
        onClick={handleOnClickLayer}
        coordinates={coordinates}
        fitBounds={true}
      >
        <Source id="meters-data" type="geojson" data={changesGeojson}>
          <Layer {...layerStyle} />
        </Source>
        <Source id="projects-data" type="geojson" data={projectsGeojson}>
          <Layer {...projectLayerStyle} />
        </Source>
      </MapContainer>
    </div>
  );
};
export default DashboardMap;
